/**
 * The desktop top bar beside the Explorer (app-clarity "IDE sidebar"): where you are as a
 * breadcrumb ("Viko › Lanes"), then Search or act (⌘K) and Settings. It spans the window above the
 * Explorer (concept `ide-home`). The project tab strip is gone at this width; the Explorer is the
 * navigation.
 */
import { Link } from '@tanstack/react-router'
import { shortcutLabel } from '@viko/transcript-ui'
import { PanelLeft, Search, Settings } from 'lucide-react'
import type { ReactNode } from 'react'
import { VMark } from '../brand.js'
import { useLocationLabel } from './StatusLine.js'

export function TopBar({
  current,
  onSearch,
  sidebarHidden,
  onToggleSidebar,
  lockChip,
  callSlot,
}: {
  /** "Locked · Unlock" while this browser's content is locked. */
  lockChip?: ReactNode
  /** Viko Call: the Call button (⌘⇧V). */
  callSlot?: ReactNode
  current?: string | undefined
  onSearch: () => void
  sidebarHidden: boolean
  onToggleSidebar: () => void
}) {
  const where = useLocationLabel()
  return (
    <header className="vs-top">
      <Link to="/home" className="vs-top__brand" aria-hidden="true" tabIndex={-1}>
        <VMark size={20} />
      </Link>
      {/* ⌘B hides the Explorer from its header; while hidden, this brings it back. */}
      {sidebarHidden ? (
        <button
          type="button"
          className="vs-top__tool"
          aria-label="Show the Explorer"
          aria-pressed={false}
          aria-keyshortcuts="Meta+B Control+B"
          title="Show the Explorer (⌘B)"
          onClick={onToggleSidebar}
        >
          <PanelLeft size={16} aria-hidden="true" />
        </button>
      ) : null}
      <p className="vs-top__crumbs">
        <span className="sr-only">You are here: </span>
        {where.map((w, i) => (
          <span key={w} className={i === where.length - 1 ? 'is-here' : undefined}>
            {i ? (
              <span className="vs-top__sep" aria-hidden="true">
                ›
              </span>
            ) : null}
            {w}
          </span>
        ))}
      </p>
      {lockChip ? <ul className="vs-top__lock">{lockChip}</ul> : null}
      <span className="vs-top__grow" />
      {callSlot}
      <button
        type="button"
        className="vs-top__search"
        aria-label="Search or act"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={onSearch}
      >
        <Search size={15} aria-hidden="true" />
        <span>Search or act</span>
        <kbd>{shortcutLabel('global.palette')}</kbd>
      </button>
      <Link
        to="/settings"
        className={`vs-top__tool lk-settings${current === 'settings' ? ' is-current' : ''}`}
        aria-label="Settings"
        aria-current={current === 'settings' ? 'page' : undefined}
        title="Settings (g ,)"
      >
        <Settings size={18} aria-hidden="true" />
      </Link>
    </header>
  )
}
